import { SourcingHiringGoal, SourcingStats } from 'api-clients/monolith';
import _round from 'lodash/round';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { durationInDays, today } from '@/utils/date-util';

import { BudgetType, CampaignParameters } from './types';

export interface HiringTarget {
  targetBudget: number;
  initialTargetBudget: number;
  projectedBudget: number;
  campaignBudget: number;
  overlapDuration: (startDate?: string, endDate?: string) => number;
  remainingDurationBudget: (
    type: BudgetType,
    duration: number,
    budget?: number,
  ) => number;
  hiringGoalSpend: number;
  preHiringGoalSpend: number;
}

interface UseHiringTargetProps {
  hookForm: UseFormReturn<CampaignParameters>;
  hiringGoalSpend: number;
  hiringGoal?: SourcingHiringGoal;
  durationStats?: SourcingStats;
  isIntegratedChannel: boolean;
  preHiringGoalSpend: number;
}

/**
 * Calculates the hiring target. Its operates at publisher level for Reach and campaign level for other channels.
 */
export function useHiringTarget({
  hookForm,
  hiringGoalSpend,
  hiringGoal,
  durationStats,
  isIntegratedChannel,
  preHiringGoalSpend,
}: UseHiringTargetProps) {
  const initialTargetBudgetRef = useRef(0);
  const {
    watch,
    formState: { isDirty, dirtyFields },
  } = hookForm;

  const inputBudget = watch('budget') || 0;
  const startsAt = watch('starts_at');
  const endAt = watch('end_at');
  const budgetType = watch('budget_type');

  /**
   * Calculates how many days remain where the campaign end date overlaps with the hiring goal's deadline.
   * If the campaign ends before today, duration is 0.
   * If the campaign starts after today, use campaign's start date otherwise use today.
   * If the campaign ends before the hiring goal, use campaign's end date.
   * Otherwise, use the hiring goal's date.
   *
   * @param startDate - The start date of the campaign.
   * @param endDate - The end date of the campaign.
   * @returns The number of days where the campaign end date overlaps with the hiring goal's deadline.
   */
  const overlapDuration = useCallback(
    (
      startDate: string | undefined = startsAt,
      endDate: string | null | undefined = endAt,
    ) => {
      if (!startDate || !endDate || !hiringGoal?.hire_by) return 0;

      if (!dirtyFields.end_at) {
        const value =
          (durationStats?.expected ?? 0) - (durationStats?.actual ?? 0);
        return Math.max(value, 0); // Ensure overlap duration is never negative
      }

      const start = new Date(startDate);
      const end = new Date(endDate);
      const goalEnd = new Date(hiringGoal.hire_by);

      if (end <= today) return 0;

      const overlapStart = start > today ? start : today;

      if (end < goalEnd) return Math.max(durationInDays(overlapStart, end), 0);

      return Math.max(durationInDays(overlapStart, goalEnd), 0);
    },
    [dirtyFields.end_at, durationStats, endAt, hiringGoal?.hire_by, startsAt],
  );

  /**
   * Converts a per-period budget (daily, weekly, monthly) into a total budget
   * over the given duration (default is the overlap duration).
   * For lifetime budget, returns the full amount directly.
   *
   * @param type - The type of budget (daily, weekly, monthly, lifetime).
   * @param duration - The duration in days.
   * @param budget - The budget per budget type.
   * @returns The total budget over the given duration.
   */
  const remainingDurationBudget = useCallback(
    (type: BudgetType, duration: number, budget: number = inputBudget) => {
      switch (type) {
        case 'weekly':
          return _round(duration * (budget / 7), 2);
        case 'monthly':
          return _round(duration * (budget / 30), 2);
        case 'daily':
          return _round(duration * budget, 2);
        case 'lifetime':
        default:
          return _round(
            inputBudget - (preHiringGoalSpend + hiringGoalSpend),
            2,
          );
      }
    },
    [hiringGoalSpend, inputBudget, preHiringGoalSpend],
  );

  /**
   * Budget projected based on current form input and overlap duration with the hiring goal.
   */
  const projectedBudget = useMemo(() => {
    if (!inputBudget) return 0;

    return remainingDurationBudget(budgetType, overlapDuration());
  }, [inputBudget, remainingDurationBudget, budgetType, overlapDuration]);

  /**
   * Total target budget including what has already been spent and the projected budget
   * for the remaining overlapping duration.
   */
  const targetBudget = useMemo(() => {
    if (isIntegratedChannel && (budgetType === 'lifetime' || !budgetType)) {
      return Math.round(inputBudget - preHiringGoalSpend) || 0;
    }

    return Math.round(projectedBudget + hiringGoalSpend) || 0;
  }, [
    isIntegratedChannel,
    budgetType,
    projectedBudget,
    hiringGoalSpend,
    inputBudget,
    preHiringGoalSpend,
  ]);

  /**
   * Calculates total campaign budget ignoring hiring goal constraints.
   * Uses remaining full campaign duration instead of hiring goal overlap.
   */
  const campaignBudget = useMemo(() => {
    if (budgetType === 'lifetime' || !budgetType)
      return Math.round(inputBudget) || 0;

    return (
      Math.round(projectedBudget + hiringGoalSpend + preHiringGoalSpend) || 0
    );
  }, [
    budgetType,
    inputBudget,
    projectedBudget,
    hiringGoalSpend,
    preHiringGoalSpend,
  ]);

  // Set the initial target budget when the form is not dirty.
  useEffect(() => {
    if (!isDirty) {
      initialTargetBudgetRef.current = targetBudget;
    }
  }, [isDirty, targetBudget]);

  return {
    targetBudget,
    projectedBudget,
    initialTargetBudget: initialTargetBudgetRef.current,
    campaignBudget,
    overlapDuration,
    remainingDurationBudget,
    preHiringGoalSpend,
    hiringGoalSpend,
  };
}
